import { fetchAuthSession } from 'aws-amplify/auth';
import EventEmitter from 'eventemitter3';
import { WS_ORIGIN_URL } from '@/constants/env.constants';
import { AbstractWsConnector, RECEIVE_MESSAGE_METHOD_NAME, WsConnectionStatus, WsSubscriptionMethod } from '@/ws/types';
import type { WorkerInboundMessage, WorkerOutboundMessage } from '@/ws/ws.worker';

export enum WsHub {
  default = 'defaulthub',
}

const STATUS_CHANGE_EVENT = 'status-change';

export class WsConnector implements AbstractWsConnector {
  private readonly worker: Worker;

  private readonly emitter = new EventEmitter();

  private readonly statusEmitter = new EventEmitter();

  private readonly pendingAcks = new Map<number, () => void>();

  private readonly ready: Promise<void>;

  private requestIdSeq = 0;

  private status: WsConnectionStatus = 'disconnected';

  constructor(hub: WsHub) {
    this.worker = new Worker(new URL('./ws.worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', (event: MessageEvent<WorkerOutboundMessage>) =>
      this.handleWorkerMessage(event.data),
    );

    this.ready = new Promise<void>((resolve) => {
      const handleReady = (event: MessageEvent<WorkerOutboundMessage>) => {
        if (event.data.type !== 'worker-ready') return;
        this.worker.removeEventListener('message', handleReady);
        this.postToWorker({ type: 'init', url: `${WS_ORIGIN_URL}/hubs/${hub}` });
        resolve();
      };
      this.worker.addEventListener('message', handleReady);
    });

    (import.meta as { hot?: { dispose: (cb: () => void) => void } }).hot?.dispose(() => this.worker.terminate());
  }

  start() {
    return this.sendCommand('start');
  }

  stop() {
    return this.sendCommand('stop');
  }

  on(handler: WsSubscriptionMethod) {
    this.emitter.on(RECEIVE_MESSAGE_METHOD_NAME, handler);
    return () => this.off(handler);
  }

  off(handler?: WsSubscriptionMethod) {
    if (handler) this.emitter.off(RECEIVE_MESSAGE_METHOD_NAME, handler);
    else this.emitter.off(RECEIVE_MESSAGE_METHOD_NAME);
  }

  invoke(eventName: string, ...args: unknown[]) {
    this.ready.then(() => this.postToWorker({ type: 'invoke', eventName, args }));
  }

  getStatus() {
    return this.status;
  }

  onStatusChange(handler: (status: WsConnectionStatus) => void) {
    this.statusEmitter.on(STATUS_CHANGE_EVENT, handler);
    return () => this.statusEmitter.off(STATUS_CHANGE_EVENT, handler);
  }

  private postToWorker(message: WorkerInboundMessage) {
    this.worker.postMessage(message);
  }

  private async sendCommand(type: 'start' | 'stop') {
    await this.ready;
    const requestId = this.requestIdSeq;
    this.requestIdSeq += 1;

    return new Promise<void>((resolve) => {
      this.pendingAcks.set(requestId, resolve);
      this.postToWorker({ type, requestId });
    });
  }

  private handleWorkerMessage(message: WorkerOutboundMessage) {
    switch (message.type) {
      case 'ack': {
        const resolveAck = this.pendingAcks.get(message.requestId);
        if (!resolveAck) return;
        this.pendingAcks.delete(message.requestId);
        resolveAck();
        break;
      }
      case 'token-request':
        this.resolveTokenRequest(message.requestId);
        break;
      case 'status':
        this.status = message.status;
        this.statusEmitter.emit(STATUS_CHANGE_EVENT, message.status);
        break;
      case 'message':
        this.emitter.emit(RECEIVE_MESSAGE_METHOD_NAME, ...message.payload);
        break;
      default:
        break;
    }
  }

  private async resolveTokenRequest(requestId: number) {
    try {
      const authSession = await fetchAuthSession();
      const token = authSession.tokens?.accessToken.toString();
      if (!token) throw new Error('Access token not found');
      this.postToWorker({ type: 'token-response', requestId, token });
    } catch (error) {
      this.postToWorker({
        type: 'token-response',
        requestId,
        error: error instanceof Error ? error.message : 'Access token not found',
      });
    }
  }
}

export const wsDefaultConnector = new WsConnector(WsHub.default);
